import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  SPWBaseSelect,
  SPWCancelButton,
  SPWFormDialog,
  SPWMainButton,
  SPWSwitch,
} from 'spw-shared/globalComponents';
import { colors } from 'spw-theme';
import makeStyles from '@mui/styles/makeStyles';
import { REPORT_TYPES } from 'spw-redux/modules/reports/constants';
import { UnitToggle } from 'spw-components/unit-conversion/unit-conversion-toggle';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  currentProject,
  selectFirstBuilding,
} from 'spw-redux/modules/projects/selectors';
import {
  currentUser as selectCurrentUser,
  selectMeasurementSystem,
} from 'spw-redux/modules/user/selectors';
import {
  FileType,
  PDF,
  PNG,
  ScenarioReportType,
} from 'spw-redux/modules/reports/types';
import {
  downloadWorksheetSpreadsheet,
  prepareAndExecuteReportDownload,
} from 'spw-redux/modules/downloads/actions';
import {
  selectCurrentScenario,
  selectTpsWorksheets,
  selectWorksheet,
} from 'spw-redux/modules/calculators/selectors';
import * as GA from 'spw-shared/analytics';
import { DEFAULT_STREET_ADDRESS, MACRO_PROGRAMMING_TYPE } from 'spw-constants';
import { getZipNamePrefix } from 'spw-shared/utils';
import _ from 'lodash';
import { selectCurrentOrganization } from 'spw-redux/modules/organization/selectors';
import { selectClientFacingNamesAreVisible } from 'spw-redux/modules/space_types/selectors';
import { selectWorksheetDecimalsAreEnabled } from 'spw-redux/modules/worksheet/selectors';
import { isTotalProgram } from 'spw-components/programming-scenarios/building-hs-sc-scenario/utils';
import { PlainWorksheet } from '../../models/Worksheet';

type Props = {
  isVisible: boolean;
  onClose: () => void;
  onExited: () => void;
};

export const RadiosControlLabel = styled(FormControlLabel)`
  width: fit-content;
  && {
    margin-left: -4px;
  }

  .MuiRadio-root {
    padding: 4px;
  }
  .MuiFormControlLabel-root {
    margin-left: 0px;
  }
  .MuiFormControlLabel-label {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
  }
`;

const useStyles = makeStyles({
  baseDialog: {
    '& .MuiDialog-paperScrollPaper': {
      maxHeight: 'calc(100vh - 48px)',
      maxWidth: '824px',
    },
  },
  form: {
    boxSizing: 'border-box',
    height: 'inherit',
    width: 343,
    '& .content': {
      maxHeight: 'calc(100vh - 180px)',
    },
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: 500,
    marginTop: 24,
    color: colors.black,
    marginBottom: 8,
    display: 'flex',
  },
  inputLabel: {
    fontSize: 14,
    marginTop: 24,
    color: colors.black,
    marginBottom: 8,
    display: 'flex',
  },
  toggleLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: colors.black,
  },
});

const WORKSHEET_WITH_NOTES_EXPORTS_OPTIONS = [
  { label: 'Worksheet', id: REPORT_TYPES.WORKSHEET },
  { label: 'Worksheet with Notes', id: REPORT_TYPES.WORKSHEET_WITH_NOTES },
  {
    label: 'Worksheet plus Summary Calculations',
    id: REPORT_TYPES.WORKSHEET_AND_SUMMARY_CALCULATIONS,
  },
];

const WORKSHEET_EXPORTS_OPTIONS = [
  { label: 'Worksheet', id: REPORT_TYPES.WORKSHEET },
  {
    label: 'Worksheet plus Summary Calculations',
    id: REPORT_TYPES.WORKSHEET_AND_SUMMARY_CALCULATIONS,
  },
];

const DEFAULT_REPORT_TYPE = { label: 'Worksheet', id: REPORT_TYPES.WORKSHEET };

const WorksheetDownloadsDialog = ({ onClose, isVisible, onExited }: Props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [fileType, setFileType] = useState<'pdf' | 'png' | 'xlsx'>(PDF);
  const scenario = useSelector(selectCurrentScenario);
  const worksheet = useSelector(selectWorksheet);
  const tpsWorksheets = useSelector(selectTpsWorksheets);
  const project = useSelector(currentProject);
  const building = useSelector(selectFirstBuilding);
  const user = useSelector(selectCurrentUser);
  const organization = useSelector(selectCurrentOrganization);
  const defaultShouldShowClientNames = useSelector(
    selectClientFacingNamesAreVisible
  );
  const [
    shouldShowClientFacingNames,
    setShouldShowClientFacingNames,
  ] = useState(defaultShouldShowClientNames);
  const defaultDecimalsAreEnabled = useSelector(
    selectWorksheetDecimalsAreEnabled
  );
  const [decimalsAreEnabled, setDecimalsAreEnabled] = useState(
    defaultDecimalsAreEnabled
  );
  const [rowsOpen, setRowsOpen] = useState(true);
  const defaultMeasurementSystem = useSelector(selectMeasurementSystem);
  const [measurementSystem, setMeasurementSystem] = useState(
    defaultMeasurementSystem
  );
  const [selectedReportType, setSelectedReportType] = useState<{
    label: string;
    id: string;
  }>(DEFAULT_REPORT_TYPE);

  const scenarioIsTotalProgram = isTotalProgram(scenario);

  const worksheetHasNotes = useMemo(() => {
    return scenarioIsTotalProgram
      ? tpsWorksheets?.some((tpsworksheet: PlainWorksheet) => {
          return tpsworksheet?.notes?.some((note) => note?.note);
        }) || worksheet?.notes?.some((note) => note.note)
      : worksheet?.notes?.some((note) => note.note);
  }, [scenarioIsTotalProgram, tpsWorksheets, worksheet]);

  useEffect(() => {
    if (
      selectedReportType?.id !==
        REPORT_TYPES.WORKSHEET_AND_SUMMARY_CALCULATIONS &&
      fileType === 'xlsx'
    ) {
      setFileType(PDF);
    }
  }, [selectedReportType, fileType]);

  const resetConfigToDefaultValues = useCallback(() => {
    setMeasurementSystem(defaultMeasurementSystem);
    setDecimalsAreEnabled(defaultDecimalsAreEnabled);
    setShouldShowClientFacingNames(defaultShouldShowClientNames);
    setRowsOpen(true);
    setSelectedReportType(DEFAULT_REPORT_TYPE);
    setFileType(PDF);
  }, [
    defaultDecimalsAreEnabled,
    defaultMeasurementSystem,
    defaultShouldShowClientNames,
  ]);

  useEffect(() => {
    if (isVisible) {
      resetConfigToDefaultValues();
    }
  }, [resetConfigToDefaultValues, isVisible]);

  const isMacro = scenario?.calculatorType === MACRO_PROGRAMMING_TYPE;

  const zipName = useMemo(() => {
    const floor =
      scenario?.floorId && building?.floors?.length
        ? _.find(building.floors, { id: scenario.floorId })
        : null;
    const name =
      building?.streetAddress &&
      building?.streetAddress !== DEFAULT_STREET_ADDRESS
        ? `${building.streetAddress}_`
        : '';

    return name
      .concat(`${scenario?.name}_`)
      .concat(
        isMacro
          ? 'Macro_'
          : getZipNamePrefix(scenario, floor ? floor.name : 'Bldg')
      )
      .concat('Worksheet.zip');
  }, [building, scenario, isMacro]);

  const handleDownload = useCallback(() => {
    onClose();
    if (fileType === 'xlsx') {
      dispatch(
        downloadWorksheetSpreadsheet({
          rowsOpen,
          showDecimals: decimalsAreEnabled,
          showClientFacingNames: shouldShowClientFacingNames,
          measurementSystem,
        })
      );
      return;
    }
    GA.event({
      category: 'Programming', // Export is only available on Programming
      subCategory: 'Worksheet',
      action: 'PROG_SHEET_EXPORT',
      label: JSON.stringify({
        projectType: project.type,
        userId: user?.id,
      }),
    });

    dispatch(
      prepareAndExecuteReportDownload(
        {
          reportType: selectedReportType.id as ScenarioReportType,
          entityId: scenario.id,
          isOpen: rowsOpen,
          format: fileType,
          shouldShowClientFacingNames,
          showDecimals: decimalsAreEnabled,
          measurementSystem,
        },
        zipName
      )
    );
  }, [
    project.type,
    user?.id,
    dispatch,
    selectedReportType?.id,
    scenario.id,
    rowsOpen,
    fileType,
    shouldShowClientFacingNames,
    zipName,
    onClose,
    decimalsAreEnabled,
    measurementSystem,
  ]);

  return (
    <SPWFormDialog
      className={classes.form}
      open={isVisible}
      onClose={onClose}
      TransitionProps={{
        onExited,
      }}
      closeAfterTransition={false}
      disableScrollLock
      dialogActions={
        <>
          <SPWMainButton
            sx={{ minWidth: 98 }}
            className="margin-right-16"
            disabled={!selectedReportType}
            onClick={handleDownload}
            data-testid="save-worksheet-download-button"
          >
            DOWNLOAD
          </SPWMainButton>
          <SPWCancelButton customColor={colors.black} onClick={onClose} />
        </>
      }
      dialogTitle="Download"
      baseDialogClassName={classes.baseDialog}
    >
      <FormControl variant="outlined" fullWidth>
        <FormLabel
          id="worksheet-download-options-label"
          className={classes.sectionLabel}
          style={{
            marginTop: 24,
          }}
        >
          WORKSHEET OPTIONS
        </FormLabel>
        <SPWBaseSelect
          fullWidth
          items={
            worksheetHasNotes
              ? WORKSHEET_WITH_NOTES_EXPORTS_OPTIONS
              : WORKSHEET_EXPORTS_OPTIONS
          }
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: '224px',
              },
            },
          }}
          onChange={(reportType) => setSelectedReportType(reportType)}
          value={selectedReportType}
          renderItem={(i) => i.label}
          variant="outlined"
          aria-labelledby="Download options select"
          name="download-options"
          data-testid="download-options-select"
        />
      </FormControl>
      {organization.hasAccessToUnitConversion && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={4}
          width="100%"
        >
          <FormLabel className={classes.toggleLabel}>
            UNITS OF MEASUREMENT
          </FormLabel>
          <UnitToggle
            system={measurementSystem}
            shouldHideLabel
            onToggle={(newSystem) => setMeasurementSystem(newSystem)}
          />
        </Box>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={4}
        width="100%"
      >
        <FormLabel className={classes.toggleLabel}>DECIMALS</FormLabel>
        <Box mr={-1.5}>
          <SPWSwitch
            checked={decimalsAreEnabled}
            onChange={() => setDecimalsAreEnabled((prev) => !prev)}
            color="default"
          />
        </Box>
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={0}
        width="100%"
      >
        <FormLabel className={classes.toggleLabel}>CLIENT NAMES </FormLabel>
        <Box mr={-1.5}>
          <SPWSwitch
            checked={shouldShowClientFacingNames}
            onChange={() => setShouldShowClientFacingNames((p) => !p)}
            color="default"
          />
        </Box>
      </Box>
      {worksheet.hasNestedRows ? (
        <Box>
          <FormLabel className={classes.sectionLabel}>WORKSHEET ROWS</FormLabel>
          <RadioGroup
            aria-labelledby="file-type-label"
            name="file-type-group"
            onChange={({ target: { value } }) => setRowsOpen(value === 'true')}
            value={rowsOpen}
          >
            <RadiosControlLabel
              label="Open"
              value
              control={<Radio size="small" />}
            />
            <RadiosControlLabel
              label="Closed"
              value={false}
              control={<Radio size="small" />}
            />
          </RadioGroup>
        </Box>
      ) : null}
      <Box mb={3}>
        <FormLabel className={classes.sectionLabel}>FILE TYPES</FormLabel>

        <RadioGroup
          aria-labelledby="file-type-label"
          name="file-type-group"
          onChange={({ target: { value } }) => setFileType(value as FileType)}
          value={fileType}
        >
          <RadiosControlLabel
            label="PDF"
            value={PDF}
            control={<Radio size="small" />}
          />
          <RadiosControlLabel
            label="PNG"
            value={PNG}
            control={<Radio size="small" color="primary" />}
          />
          <RadiosControlLabel
            label="XLSX (Excel)"
            value="xlsx"
            disabled={
              selectedReportType?.id !==
              REPORT_TYPES.WORKSHEET_AND_SUMMARY_CALCULATIONS
            }
            control={<Radio size="small" />}
          />
        </RadioGroup>
      </Box>
    </SPWFormDialog>
  );
};

export default WorksheetDownloadsDialog;
